import React, { useState } from "react";
import { Calculator } from "lucide-react";
import GestionaleShell from "./GestionaleShell";
import GestCrudTable from "./GestCrudTable";

const FIELDS = [
  { key: "sku_name", label: "SKU / Prodotto", type: "text", required: true, span: 2 },
  { key: "supplier_name", label: "Fornitore", type: "text" },
  { key: "category", label: "Categoria", type: "select", options: [
    { value: "wine", label: "Vino" }, { value: "food", label: "Food" }, { value: "mixed", label: "Misto" },
  ], defaultValue: "wine" },
  { key: "quantity", label: "Quantità", type: "number", step: "1", defaultValue: 1 },
  { key: "exw_eur", label: "EXW (€)", type: "number" },
  { key: "freight_eur", label: "Freight (€)", type: "number", hideInTable: true },
  { key: "customs_eur", label: "Dogana (€)", type: "number", hideInTable: true },
  { key: "broker_eur", label: "Broker (€)", type: "number", hideInTable: true },
  { key: "fx_rate", label: "Cambio EUR→BRL", type: "number", step: "0.01", defaultValue: 5.5, hideInTable: true },
  { key: "sell_price_brl", label: "Prezzo BRL", type: "number" },
  { key: "target_margin_pct", label: "Margine target %", type: "number", step: "0.5", defaultValue: 25, hideInTable: true },
  { key: "status", label: "Stato", type: "select", statusTone: true, options: [
    { value: "draft", label: "Bozza", tone: "stone" },
    { value: "sent", label: "Inviata", tone: "blue" },
    { value: "won", label: "Vinta", tone: "emerald" },
    { value: "lost", label: "Persa", tone: "rose" },
  ], defaultValue: "draft" },
  { key: "notes", label: "Note", type: "textarea", hideInTable: true },
];

const TABLE_FIELDS = [
  { key: "margin_pct_calc", label: "Margine %", type: "text", format: (_, it) => `${it.metrics?.margin_pct ?? 0}%` },
  { key: "total_revenue_calc", label: "Revenue (€)", type: "text", format: (_, it) => `€ ${(it.metrics?.total_revenue_eur ?? 0).toLocaleString("it-IT")}` },
];

export default function QuotationsPage() {
  const [data, setData] = useState({ total: 0, items: [] });
  const won = (data.items || []).filter(i => i.status === "won").length;
  const totalRev = (data.items || []).reduce((s, i) => s + (i.metrics?.total_revenue_eur || 0), 0);

  // We embed metric columns at the start by composing fields
  const fieldsWithMetrics = [
    FIELDS[0], FIELDS[1], FIELDS[2], FIELDS[3], FIELDS[4],
    ...TABLE_FIELDS,
    ...FIELDS.slice(5),
  ];

  return (
    <GestionaleShell
      icon={Calculator}
      code="03"
      title="Quotation & Deal Desk"
      subtitle="Cost simulation EXW/FOB/Freight + margine target. Cambio EUR↔BRL incorporato."
      kpiBlocks={[
        { value: data.total || 0, label: "Quotation totali" },
        { value: `€${Math.round(totalRev / 1000)}k`, label: "Revenue potenziale" },
      ]}
    >
      <GestCrudTable
        endpoint="/gestionale/quotations"
        fields={fieldsWithMetrics}
        itemLabel="quotation"
        emptyHint="Crea la prima offerta indicando costi EUR e prezzo BRL — il sistema calcola automaticamente il margine."
        testIdPrefix="quotations"
        onItemsLoaded={(d) => setData(d)}
        rowSummary={(items) => {
          const wonItems = items.filter(i => i.status === "won");
          const wonRev = wonItems.reduce((s, i) => s + (i.metrics?.total_revenue_eur || 0), 0);
          const wonMargin = wonItems.reduce((s, i) => s + (i.metrics?.total_margin_eur || 0), 0);
          return (
            <div className="flex flex-wrap gap-4 text-[11px] uppercase tracking-[0.18em]">
              <span className="text-stone-500">Vinte: <b className="text-stone-900">{wonItems.length}</b></span>
              <span className="text-stone-500">Revenue won: <b className="text-emerald-700">€{wonRev.toLocaleString("it-IT")}</b></span>
              <span className="text-stone-500">Margine won: <b className="text-[#A88A4A]">€{wonMargin.toLocaleString("it-IT")}</b></span>
              <span className="ml-auto text-stone-400">Win rate: {items.length > 0 ? Math.round(wonItems.length / items.length * 100) : 0}%</span>
            </div>
          );
        }}
      />
    </GestionaleShell>
  );
}
